import { useState } from 'react'
import type { ChangeEvent, ReactNode } from 'react'
import { ApiFailure, NetworkDown, NotSignedIn } from '../api/client'
import { useI18n } from '../i18n/I18nProvider'
import type { ReferenceItem } from '../api/types'

export function Field({
  label,
  hint,
  required,
  error,
  children,
}: {
  label: string
  hint?: string
  required?: boolean
  error?: string | undefined
  children: ReactNode
}) {
  return (
    <label className="field">
      <span className="field-label">
        {label}
        {required ? <span className="field-required" aria-hidden="true"> *</span> : null}
      </span>
      {children}
      {hint ? <span className="field-hint">{hint}</span> : null}
      {error ? <span className="field-error">{error}</span> : null}
    </label>
  )
}

export function TextInput({
  value,
  onChange,
  type = 'text',
  ...rest
}: {
  value: string
  onChange: (value: string) => void
  type?: string
  placeholder?: string
  autoComplete?: string
  maxLength?: number
  required?: boolean
}) {
  return (
    <input
      className="control"
      type={type}
      value={value}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
      {...rest}
    />
  )
}

/**
 * A password box with the eye that shows what was typed.
 *
 * Worth the extra control rather than `type="password"` on TextInput: a password that cannot be
 * read back is retyped from memory, and the people most likely to get it wrong are the ones using
 * a long passphrase — which is exactly what auth.passwordHint asks them to use. On a phone, where
 * the keyboard hides half the screen and an autocorrected character is invisible, it is the
 * difference between signing in and giving up.
 *
 * Two details that are easy to get wrong and silent when you do:
 *
 *   * `type="button"`. Inside a form, a button with no type is a submit button, so tapping the eye
 *     would post the form with a half-typed password and spend one of the login attempts.
 *   * the eye is a real <button>, which is interactive content, so the surrounding <label> does
 *     not forward the click to the input as it does for an icon or a span.
 *
 * Revealing is per field and resets with the page; nothing is remembered.
 */
export function PasswordInput({
  value,
  onChange,
  autoComplete,
  required,
}: {
  value: string
  onChange: (value: string) => void
  autoComplete?: string
  required?: boolean
}) {
  const { t } = useI18n()
  const [revealed, setRevealed] = useState(false)

  const label = revealed ? t('auth.passwordHide') : t('auth.passwordShow')

  return (
    <span className="password-field">
      <input
        className="control"
        type={revealed ? 'text' : 'password'}
        value={value}
        onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        autoComplete={autoComplete}
        required={required}
      />
      <button
        type="button"
        className="password-reveal"
        onClick={() => setRevealed((shown) => !shown)}
        aria-label={label}
        aria-pressed={revealed}
        title={label}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
          <path
            d="M1.5 12S5.5 5 12 5s10.5 7 10.5 7-4 7-10.5 7S1.5 12 1.5 12Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
          {revealed ? (
            <path d="M4 20 20 4" fill="none" stroke="currentColor" strokeWidth="1.6" />
          ) : null}
        </svg>
      </button>
    </span>
  )
}

export function NumberInput({
  value,
  onChange,
  min,
  max,
}: {
  value: number | null
  onChange: (value: number | null) => void
  min?: number
  max?: number
}) {
  return (
    <input
      className="control"
      type="number"
      value={value ?? ''}
      min={min}
      max={max}
      onChange={(event) => {
        const raw = event.target.value
        onChange(raw === '' ? null : Number(raw))
      }}
    />
  )
}

export function TextArea({
  value,
  onChange,
  rows = 5,
  maxLength,
}: {
  value: string
  onChange: (value: string) => void
  rows?: number
  maxLength?: number
}) {
  return (
    <textarea
      className="control"
      rows={rows}
      maxLength={maxLength}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  )
}

/**
 * A dropdown over reference data. It renders whichever name matches the current language, which
 * is why a language switch never needs another request: the API sends both (契约 §8).
 */
export function ReferenceSelect({
  value,
  onChange,
  items,
  allowEmpty = true,
}: {
  value: string | null
  onChange: (value: string | null) => void
  items: readonly ReferenceItem[]
  allowEmpty?: boolean
}) {
  const { t, pick } = useI18n()

  return (
    <select
      className="control"
      value={value ?? ''}
      onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
    >
      {allowEmpty ? <option value="">{t('common.choose')}</option> : null}
      {[...items]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((item) => (
          <option key={item.id} value={item.id}>
            {pick(item)}
          </option>
        ))}
    </select>
  )
}

export function MultiReferenceSelect({
  values,
  onChange,
  items,
}: {
  values: readonly string[]
  onChange: (values: string[]) => void
  items: readonly ReferenceItem[]
}) {
  const { pick } = useI18n()
  const chosen = new Set(values)

  return (
    <div className="chips">
      {[...items]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((item) => {
          const on = chosen.has(item.id)

          return (
            <button
              key={item.id}
              type="button"
              className={on ? 'chip chip-on' : 'chip'}
              aria-pressed={on}
              onClick={() =>
                onChange(on ? values.filter((v) => v !== item.id) : [...values, item.id])
              }
            >
              {pick(item)}
            </button>
          )
        })}
    </div>
  )
}

export function YesNo({
  value,
  onChange,
}: {
  value: boolean | null
  onChange: (value: boolean | null) => void
}) {
  const { t } = useI18n()

  return (
    <select
      className="control"
      value={value === null ? '' : value ? 'yes' : 'no'}
      onChange={(event) => {
        const raw = event.target.value
        onChange(raw === '' ? null : raw === 'yes')
      }}
    >
      <option value="">{t('common.choose')}</option>
      <option value="yes">{t('common.yes')}</option>
      <option value="no">{t('common.no')}</option>
    </select>
  )
}

export function Busy({ label }: { label?: string }) {
  const { t } = useI18n()

  return (
    <p className="busy" role="status">
      {label ?? t('common.loading')}
    </p>
  )
}

/**
 * Turns whatever went wrong into one sentence.
 *
 * The server's message is used when there is one: it was written for this situation and is more
 * specific than anything a generic banner could say. The `traceId` is shown for the cases nobody
 * anticipated, so a support message can point straight at the request in the log.
 */
export function ErrorBanner({ error }: { error: unknown }) {
  const { t } = useI18n()

  if (!error) {
    return null
  }

  if (error instanceof NotSignedIn) {
    return <p className="banner banner-error">{t('error.signedOut')}</p>
  }

  if (error instanceof NetworkDown) {
    return <p className="banner banner-error">{t('error.network')}</p>
  }

  if (error instanceof ApiFailure) {
    const fields = Object.values(error.fieldErrors).flat()

    return (
      <div className="banner banner-error">
        <p>{error.message}</p>
        {fields.length > 0 ? (
          <ul>
            {fields.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        ) : null}
      </div>
    )
  }

  return <p className="banner banner-error">{t('error.unexpected')}</p>
}

export function Notice({ children }: { children: ReactNode }) {
  return <p className="banner banner-notice">{children}</p>
}
